/**
 * Hurtiggodkjenning av et årshjul-generert artikkelutkast.
 *
 * Åpnes fra push-varsel (type `article_review`) eller dyplenken
 * `?review_article=<id>`. Målet er ETT trykk fra «AI har skrevet et utkast» til
 * publisert — uten omveien om den fulle editoren.
 *
 * Publisering herfra kjører hele løypa: oversettelse til alle appens språk og,
 * hvis artikkelen er merket for det, e-post til hele brukerbasen. Derfor står
 * mottakerantallet i klartekst på knappen. Ett trykk skal gjøre det TRYGGE
 * raskt, ikke det farlige lett.
 */
import React, { useEffect, useState } from 'react';
import { Check, Clock, Loader2, Trash2, X, Mail, PencilLine, AlertTriangle } from 'lucide-react';
import type { Article } from '../../types';
import {
  subscribeToArticle,
  reviewArticle,
  countArticleEmailRecipients,
  type ReviewArticleAction,
} from '../../services/articleService';
import { formatWheelDate } from '../../utils/articleWheelDates';
import { Button } from '../ui/Button';

interface Props {
  articleId: string;
  onClose: () => void;
  /** Åpne den fulle editoren i stedet — for de gangene teksten må rettes. */
  onOpenEditor?: (article: Article) => void;
}

function plainTextExcerpt(html: string, max = 400): string {
  const text = String(html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

const ArticleQuickReviewModal: React.FC<Props> = ({ articleId, onClose, onOpenEditor }) => {
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<ReviewArticleAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recipients, setRecipients] = useState<number | null>(null);
  const [confirmReject, setConfirmReject] = useState(false);

  // Live: er utkastet alt behandlet fra en annen enhet, skal modalen si fra i
  // stedet for å la admin trykke på noe som ikke finnes lenger.
  useEffect(() => {
    const unsub = subscribeToArticle(articleId, (a) => {
      setArticle(a);
      setLoading(false);
    });
    return () => unsub();
  }, [articleId]);

  const willEmail = article?.distribute_via_email === true;

  useEffect(() => {
    if (!willEmail) return;
    let alive = true;
    countArticleEmailRecipients().then((n) => { if (alive) setRecipients(n); });
    return () => { alive = false; };
  }, [willEmail]);

  const run = async (action: ReviewArticleAction) => {
    setBusy(action);
    setError(null);
    try {
      await reviewArticle(articleId, action, article?.occasion_ymd);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Handlingen feilet.');
      setBusy(null);
    }
  };

  const heroImage = article?.images?.length
    ? [...article.images].sort((a, b) => (a.order || 0) - (b.order || 0))[0].url
    : null;
  const scheduleYmd = article?.occasion_ymd || '';
  const isGenerating = article?.status === 'generating';
  const alreadyHandled = !!article && !['pending_review', 'draft', 'generating'].includes(article.status);

  return (
    <div className="fixed inset-0 z-[10010] flex items-end sm:items-center justify-center p-3 sm:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div
        className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto custom-scrollbar bg-[var(--bg-surface)] border border-[var(--border-primary)] rounded-card shadow-2xl animate-in slide-in-from-bottom-4 duration-200"
        role="dialog"
        aria-modal="true"
        aria-labelledby="article-review-title"
      >
        <div className="flex items-start justify-between gap-3 p-4 pb-2">
          <div className="min-w-0">
            <p className="text-3xs font-black uppercase tracking-widest text-emerald-400">Til godkjenning</p>
            <h2 id="article-review-title" className="text-base font-black text-[var(--text-primary)] leading-snug">
              {loading ? 'Henter utkast…' : article?.title || 'Ikke funnet'}
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Lukk"
            className="p-2 rounded-card text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)] transition-colors flex-shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-4 pb-4 space-y-3">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-12 text-[var(--text-muted)] text-tiny">
              <Loader2 size={18} className="animate-spin" /> Henter utkast…
            </div>
          )}

          {!loading && !article && (
            <p className="text-tiny text-[var(--text-muted)] py-8 text-center">
              Dette utkastet finnes ikke lenger — det er antakelig allerede behandlet.
            </p>
          )}

          {!loading && isGenerating && (
            <div className="flex items-center gap-2 py-8 justify-center text-emerald-400 text-tiny font-bold">
              <Loader2 size={16} className="animate-spin" /> AI skriver fortsatt — prøv igjen om et minutt.
            </div>
          )}

          {!loading && alreadyHandled && (
            <div className="flex items-start gap-2 p-3 rounded-card bg-amber-500/10 border border-amber-500/20 text-amber-400">
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
              <p className="text-xs font-bold">
                Allerede behandlet (status: {article!.status}). Ingen handling nødvendig.
              </p>
            </div>
          )}

          {article && !isGenerating && !alreadyHandled && (
            <>
              {heroImage && (
                <img src={heroImage} alt="" className="w-full aspect-video object-cover rounded-card" />
              )}

              {article.summary && (
                <p className="text-tiny text-[var(--text-secondary)] font-bold leading-relaxed">{article.summary}</p>
              )}

              <p className="text-xs text-[var(--text-muted)] leading-relaxed">
                {plainTextExcerpt(article.content)}
              </p>

              <div className="flex flex-wrap gap-1.5">
                {article.target_audience && (
                  <span className="text-4xs font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">
                    {article.target_audience}
                  </span>
                )}
                {article.wheel_entry_id && (
                  <span className="text-4xs font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-[var(--bg-surface-alt)] text-[var(--text-muted)]">
                    Årshjul · {article.wheel_entry_id}
                  </span>
                )}
                {scheduleYmd && (
                  <span className="text-4xs font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-[var(--bg-surface-alt)] text-[var(--text-muted)]">
                    <Clock size={9} className="inline mr-1 -mt-0.5" />{formatWheelDate(scheduleYmd)}
                  </span>
                )}
              </div>

              {willEmail && (
                <div className="flex items-start gap-2 p-3 rounded-card bg-amber-500/10 border border-amber-500/20 text-amber-400">
                  <Mail size={14} className="flex-shrink-0 mt-0.5" />
                  <p className="text-xs font-bold leading-relaxed">
                    Publisering sender e-post til{' '}
                    {recipients === null ? 'alle med varsler på' : `${recipients.toLocaleString('nb-NO')} mottakere`}.
                    Skru av e-post i editoren hvis du bare vil publisere på nett.
                  </p>
                </div>
              )}

              {error && (
                <p className="text-xs font-bold text-red-400 bg-red-500/10 rounded-card px-3 py-2">{error}</p>
              )}

              <div className="space-y-2 pt-1">
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  onClick={() => run('publish_now')}
                  disabled={!!busy}
                  loading={busy === 'publish_now'}
                  icon={<Check size={16} />}
                >
                  {willEmail && recipients !== null
                    ? `Publiser nå · e-post til ${recipients.toLocaleString('nb-NO')}`
                    : 'Publiser nå'}
                </Button>

                {scheduleYmd && (
                  <Button
                    variant="secondary"
                    size="md"
                    fullWidth
                    onClick={() => run('approve_scheduled')}
                    disabled={!!busy}
                    loading={busy === 'approve_scheduled'}
                    icon={<Clock size={14} />}
                  >
                    Godkjenn for {formatWheelDate(scheduleYmd)}
                  </Button>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={() => run('later')}
                    disabled={!!busy}
                    loading={busy === 'later'}
                  >
                    Senere
                  </Button>
                  <Button
                    variant="danger"
                    size="md"
                    onClick={() => (confirmReject ? run('reject') : setConfirmReject(true))}
                    disabled={!!busy}
                    loading={busy === 'reject'}
                    icon={<Trash2 size={14} />}
                  >
                    {confirmReject ? 'Slett for godt?' : 'Avvis'}
                  </Button>
                </div>

                {onOpenEditor && (
                  <Button
                    variant="ghost"
                    size="sm"
                    fullWidth
                    onClick={() => { onOpenEditor(article); onClose(); }}
                    disabled={!!busy}
                    icon={<PencilLine size={12} />}
                  >
                    Åpne i full editor
                  </Button>
                )}
              </div>

              <p className="text-tiny text-[var(--text-muted)] text-center leading-relaxed pt-1">
                «Senere» legger utkastet tilbake i vanlig utkastliste. «Avvis» sletter det.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArticleQuickReviewModal;
